import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type { CallAiInsightsModel } from '../dto/softphone/call-ai-insights.dto';
import { BaseEntity } from './base.entity';
import { CallUserEntity } from './call-user.entity';
import { UserEntity } from './user.entity';

@Index('call__fk_user__id_fk', ['fkUserId'], {})
@Index('call_externalId_uindex', ['externalId'], { unique: true })
@Index('call_callerIdMatch_idx', ['callerIdMatchType', 'callerIdMatchEntityId'])
@Index('call_repCallSid_idx', ['repCallSid'])
@Entity('call')
export class CallEntity extends BaseEntity {
	@Column('varchar', {
		name: 'externalId',
		nullable: true,
		unique: true,
		length: 64,
	})
	public externalId!: string | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', { name: 'result', nullable: true, length: 255 })
	public result!: string | null;

	@Column('text', { name: 'notes', nullable: true })
	public notes!: string | null;

	@Column('text', { name: 'ai_summary', nullable: true })
	public aiSummary!: string | null;

	/**
	 * Structured post-call AI analysis (PD-14417): sentiment, key
	 * points, and detected commitments (wrap-up suggested actions).
	 * Written by the post-call processor alongside `aiSummary`; null
	 * for calls processed before this landed or when extraction
	 * fell back to plain text.
	 */
	@Column('json', { name: 'aiInsights', nullable: true })
	public aiInsights!: CallAiInsightsModel | null;

	/**
	 * Full text transcript of the call recording (DD.11.2). Produced
	 * by the post-call processing pipeline: Deepgram pre-recorded
	 * transcription on the Twilio recording bytes, written here when
	 * the job completes. Null when:
	 *   - Recording is off for the line, or
	 *   - The post-call job is still in flight, or
	 *   - The transcription failed (transcriptionStatus carries the
	 *     failure for retry / observability)
	 *
	 * `aiSummary` (above) is the LLM summary derived from this text;
	 * usually populated together a few seconds after the transcript
	 * lands.
	 */
	@Column('text', { name: 'transcriptText', nullable: true })
	public transcriptText!: string | null;

	/**
	 * Structured per-turn transcript (DD.12.0b). When the recording is
	 * dual-channel (`recordingChannels === 'dual'`), Deepgram returns
	 * per-channel utterances which we interleave chronologically and
	 * store here. Used by the Communications-tab call viewer to render
	 * the transcript as chat bubbles labelled by speaker.
	 *
	 * Each turn:
	 *   { channel: 0 | 1, startSec, endSec, text }
	 *
	 * `channel` is the deterministic channel index from Twilio's
	 * dual-channel recording — 0 = inbound caller leg, 1 = outbound
	 * (rep) leg. Speaker NAMES are NOT stored here; they're resolved
	 * at render time by joining `fkUser` (rep) and the matched
	 * client/vendor (caller). Storing names would invalidate on rep
	 * renames or contact merges.
	 *
	 * Null on:
	 *   - Pre-DD.12.0b calls (forward-only — no backfill)
	 *   - Mono / single-channel recordings (e.g. AI-agent recordings,
	 *     conference legs, legacy paths) — `transcriptText` still
	 *     populated, just no per-turn breakdown
	 *   - Calls where the post-call processor hasn't completed yet
	 */
	@Column('json', { name: 'transcriptTurns', nullable: true })
	public transcriptTurns!: Array<{
		channel: 0 | 1;
		startSec: number;
		endSec: number;
		text: string;
	}> | null;

	/**
	 * Free-form per-call extension (mirrors `chat_conversation.metadata`).
	 * Currently holds the AI↔human voice-handoff state — see
	 * `CallAiHandoffStateModel`: `aiPaused` (the AI runtime stops engaging
	 * the call when true) and `activeAiAgentId` (which AI agent currently
	 * owns the call). `VoiceHandoffService` is the single writer.
	 */
	@Column('json', { name: 'metadata', nullable: true })
	public metadata?: Record<string, unknown>;

	/**
	 * Tracks the post-call processing pipeline status. `null` ==
	 * untouched (no recording, or recording happened pre-feature).
	 * Other states drive UI affordances (loading vs available vs
	 * retry button) and let the processor short-circuit on retry.
	 */
	@Column('varchar', {
		name: 'transcriptionStatus',
		nullable: true,
		length: 32,
	})
	public transcriptionStatus!:
		| 'queued'
		| 'transcribing'
		| 'summarizing'
		| 'completed'
		| 'failed'
		| null;

	/**
	 * Caller-ID match recorded at TwiML-routing time for inbound
	 * softphone calls. Null on outbound calls and on legacy imported
	 * rows. Populated from `CallerIdMatchResultDto.best`.
	 */
	@Column('varchar', {
		name: 'callerIdMatchType',
		nullable: true,
		length: 16,
	})
	public callerIdMatchType!: 'client' | 'vendor' | null;

	@Column('int', {
		name: 'callerIdMatchEntityId',
		nullable: true,
		unsigned: true,
	})
	public callerIdMatchEntityId!: number | null;

	/**
	 * Disposition columns populated by the post-call wrap-up flow.
	 * The rep picks a code from a short preset list (or types a
	 * custom one) plus optional free-form notes; the panel can also
	 * flag a follow-up commitment with a date.
	 *
	 * `result` (existing column) carries the call-lifecycle outcome
	 * stamped by the status webhook (`completed`, `no-answer`, etc.) —
	 * `dispositionCode` is the rep's BUSINESS verdict on what
	 * happened ("Spoke to customer", "Wrong number", "Follow-up
	 * needed"). Don't conflate them.
	 */
	@Column('varchar', {
		name: 'dispositionCode',
		nullable: true,
		length: 64,
	})
	public dispositionCode!: string | null;

	@Column('text', { name: 'dispositionNote', nullable: true })
	public dispositionNote!: string | null;

	@Column('tinyint', {
		name: 'requiresFollowUp',
		width: 1,
		default: () => "'0'",
	})
	public requiresFollowUp!: boolean;

	@Column('datetime', { name: 'followUpDate', nullable: true })
	public followUpDate!: Date | null;

	/**
	 * The follow-up TASK (`_event` row) created from this call's wrap-up
	 * (PD-14437). Doubles as the idempotency guard — a re-saved wrap-up
	 * won't create a second task while this is set — and gives
	 * re-attribution (PD-14419) a handle to retarget the task. The
	 * denormalized requiresFollowUp/followUpDate columns above stay
	 * maintained regardless (cheap earliest-follow-up list filters).
	 */
	@Column('int', {
		name: 'fk__event__id_followUp',
		nullable: true,
		unsigned: true,
	})
	public followUpEventId!: number | null;

	/**
	 * Twilio Recording metadata. Populated by the
	 * `recording-status` webhook when the recording finishes; null
	 * for calls that weren't recorded (tenant flag off) and for
	 * legacy imported call rows.
	 *
	 * `recordingUrl` is Twilio's CDN URL — we never expose it
	 * directly to the browser. The recording playback endpoint
	 * proxies authenticated reads through our master Twilio creds
	 * after a permission check (rep / manager / admin), so a leaked
	 * URL doesn't bypass authorization.
	 */
	@Column('varchar', {
		name: 'recordingSid',
		nullable: true,
		length: 64,
	})
	public recordingSid!: string | null;

	@Column('varchar', {
		name: 'recordingUrl',
		nullable: true,
		length: 512,
	})
	public recordingUrl!: string | null;

	@Column('int', {
		name: 'recordingDurationSeconds',
		nullable: true,
		unsigned: true,
	})
	public recordingDurationSeconds!: number | null;

	@Column('varchar', {
		name: 'recordingChannels',
		nullable: true,
		length: 8,
	})
	public recordingChannels!: string | null;

	/**
	 * Conference architecture (Phase 2.4) — populated when a call gets
	 * "promoted" from a direct `<Dial><Client>` bridge to a Twilio
	 * Conference room. The promotion happens at the first `holdCall`
	 * request: instead of the legacy redirect-based hold (which dropped
	 * the rep's leg and re-rang their browser on unhold), we redirect
	 * BOTH legs to a Conference and use participant-level
	 * `Hold=true|false` updates for subsequent hold/unhold operations.
	 *
	 * `conferenceName` is generated server-side once per call
	 * (`softphone-call-{dbCallId}`) and is the join key for both legs.
	 *
	 * `conferenceSid` is Twilio's identifier for the conference room,
	 * which only exists once participants have joined; it's set by the
	 * conference-status webhook on the first `participant-join` event.
	 *
	 * Both fields are null until the call is promoted; outbound and
	 * legacy calls keep them null forever.
	 */
	@Column('varchar', {
		name: 'conferenceName',
		nullable: true,
		length: 128,
	})
	public conferenceName!: string | null;

	@Column('varchar', {
		name: 'conferenceSid',
		nullable: true,
		length: 64,
	})
	public conferenceSid!: string | null;

	/**
	 * The rep's Twilio CallSid — the leg dialed into the Conference via
	 * `client.calls.create({ to: 'client:identity', ... })` after the
	 * customer's TwiML response landed them in the conference room.
	 *
	 * Kept distinct from `externalId` (which is the customer's CallSid)
	 * because the two legs have separate lifecycles: the rep can hang
	 * up (`repCallSid` becomes a stale-then-cleared identifier) while
	 * the customer stays parked in the conference, and a transfer adds
	 * NEW participant CallSids that aren't either of these.
	 *
	 * The browser uses this SID to fetch its call-pop context on the
	 * SDK's `incoming` event (`GET /api/softphone/calls/incoming-context
	 * ?repCallSid=...`) — Conference architecture loses the
	 * `<Parameter>` channel that the legacy `<Dial><Client>` flow used
	 * to deliver matcher data inline.
	 */
	@Column('varchar', {
		name: 'repCallSid',
		nullable: true,
		length: 64,
	})
	public repCallSid!: string | null;

	/**
	 * Outbound-only — the CallSid of the leg the routing service
	 * created via REST API to dial the target into the conference.
	 * Counterpart to `repCallSid` for outbound calls: hold/unhold
	 * needs to flip the participant flag on the OTHER party (not the
	 * rep), and for outbound that "other party" is this leg, not
	 * `externalId` (which is the rep's SDK-initiated outbound call).
	 *
	 * Stays null for inbound calls; hold/unhold for inbound uses
	 * `externalId` directly since that's already the customer's leg.
	 */
	@Column('varchar', {
		name: 'targetCallSid',
		nullable: true,
		length: 64,
	})
	public targetCallSid!: string | null;

	/**
	 * True when the customer was routed to voicemail instead of being
	 * connected to a rep. Set when the rep doesn't answer (rep-leg
	 * status webhook fires `no-answer`/`canceled`/`failed`/`busy`)
	 * and we redirect the customer's leg to a `<Record>` TwiML.
	 *
	 * The recording itself lands on the existing `recordingSid` /
	 * `recordingUrl` columns via the same recording-status webhook
	 * the in-call recordings use — voicemail is just a recording with
	 * `isVoicemail=true`. Inbox UI can filter on this to surface
	 * voicemails as their own queue.
	 */
	@Column('tinyint', {
		name: 'isVoicemail',
		width: 1,
		default: () => "'0'",
	})
	public isVoicemail!: boolean;

	/**
	 * The tenant-owned phone number the customer dialed (`body.To`
	 * from the inbound webhook), in E.164. Persisted on inbound call
	 * creation so downstream resolvers (DD.4.F no-answer fallback)
	 * can look up the `SoftphoneNumberEntity` → `SoftphoneNumberConfigEntity`
	 * chain that owns this call without re-querying Twilio.
	 *
	 * Null on legacy rows (pre-DD.4.F) and on outbound calls (where
	 * the "dialed-in" concept doesn't apply).
	 */
	@Column('varchar', {
		name: 'phoneTo',
		nullable: true,
		length: 32,
	})
	public phoneTo!: string | null;

	@ManyToOne(() => UserEntity, (user) => user.calls, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	@OneToMany(() => CallUserEntity, (callUser) => callUser.fkCall, {})
	public callUsers!: Array<CallUserEntity>;
}
